import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

import { officeLocations } from '../../../utils/locationData';
import mapStyle from '../../../assets/json/mapStyle.json';
import londonImage from '../../../assets/images/contact/london-hq.jpg';
import dubaiImage from '../../../assets/images/contact/dubai.jpg';
import singaporeImage from '../../../assets/images/contact/singapore.jpg';

// Load Google Maps script using API key from .env
const loadGoogleMapsScript = callback => {
  const existingScript = document.getElementById('googleMapsApi');
  if (!existingScript) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_SECRET_KEY;
    if (!apiKey) {
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.id = 'googleMapsApi';
    document.body.appendChild(script);

    script.onload = () => {
      if (callback) callback();
    };
  } else if (callback) callback();
};

const MapComponent = ({ selectedOffice = null, showControls = true }) => {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowsRef = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const worldMap = useRef({
    center: { lat: 30, lng: 20 },
    zoom: 2
  });

  // Initialize map when script is loaded
  useEffect(() => {
    loadGoogleMapsScript(() => {
      setMapLoaded(true);
    });
  }, []);

  // Create and configure the map
  useEffect(() => {
    if (!mapLoaded || !window.google || !window.google.maps) return;

    // Create map with world view initially
    const mapOptions = {
      zoom: worldMap.current.zoom,
      center: worldMap.current.center,
      styles: mapStyle,
      disableDefaultUI: true,
      zoomControl: showControls,
      fullscreenControl: showControls
    };

    const map = new window.google.maps.Map(mapRef.current, mapOptions);
    googleMapRef.current = map;

    // Create custom marker SVGs
    const createMarkerIcon = isHQ => {
      const color = isHQ ? '#649fef' : '#1A1A1A';
      const size = isHQ ? 38 : 34; // Slightly larger icon for HQ

      return {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: color,
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: 'white',
        scale: size / 24,
        anchor: new window.google.maps.Point(12, 22)
      };
    };
    const createInfoWindowContent = office => {
      const backgroundColor = office.isHeadquarters ? '#649fef' : '#1A1A1A';
      let officeImage = '';
      let officeAlt = '';
      switch (office.id) {
        case 'london':
          officeImage = londonImage;
          officeAlt = 'London HQ';
          break;
        case 'dubai':
          officeImage = dubaiImage;
          officeAlt = 'Dubai Office';
          break;
        case 'singapore':
          officeImage = singaporeImage;
          officeAlt = 'Singapore Office';
          break;
        default:
          officeImage = '';
          officeAlt = office.name;
      }

      // Responsive image style
      const imageStyle =
        'width: 100%; max-width: 220px; height: auto; border-radius: 6px; margin-bottom: 10px; box-shadow: 0 2px 8px rgba(0,0,0,0.10);';

      return `
        <div class="custom-info-window" style="
          padding: 12px 16px;
          min-width: 160px;
          border-radius: 6px;
          background-color: ${backgroundColor};
          color: white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          position: relative;
          text-align: center;
        ">
          <div class="info-window-close" style="
            position: absolute;
            top: 4px;
            right: 4px;
            width: 20px;
            height: 20px;
            background-color: white;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
          ">
            <span style="
              font-size: 12px;
              font-weight: bold;
              color: #1A1A1A;
              line-height: 1;
            ">✕</span>
          </div>
          ${officeImage ? `<img src='${officeImage}' alt='${officeAlt}' style='${imageStyle}' />` : ''}
          <div style="font-weight: 600; margin-bottom: 4px; font-size: 14px;">${office.name}</div>
          <div style="font-size: 11px; opacity: 0.9;">${office.isHeadquarters ? 'MarQ Capital HQ' : 'MarQ Capital Office'}</div>
          <div style="
            position: absolute;
            bottom: -6px;
            left: 50%;-
            transform: translateX(-50%);
            width: 0;
            height: 0;
          "></div>
        </div>
      `;
    };

    // Add global styles to hide Google Maps InfoWindow elements
    const injectGlobalStyles = () => {
      const styleId = 'custom-map-styles';

      // Remove existing style if any
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) existingStyle.remove();

      // Create and inject new styles
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.innerHTML = `
        /* Hide default UI elements from Google Maps InfoWindow */
        .gm-style .gm-style-iw-c {
          padding: 0 !important;
          box-shadow: none !important;
          border-radius: 6px !important;
          background-color: transparent !important;
          max-width: 250px !important;
        }
        
        /* Hide the default close button */
        .gm-style .gm-style-iw-t button {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        
        /* Hide all close buttons except our custom one */
        .gm-ui-hover-effect {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
        
        /* Hide the default infowindow tail */
        .gm-style .gm-style-iw-tc {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Remove all padding and background */
        .gm-style .gm-style-iw-d {
          overflow: visible !important;
          max-height: none !important;
          padding: 0 !important;
          background-color: transparent !important;
        }
        
        /* Remove scrollbar */
        .gm-style .gm-style-iw-d::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        
        .gm-style .gm-style-iw-d::-webkit-scrollbar-track {
          display: none !important;
        }
        
        .gm-style .gm-style-iw-d::-webkit-scrollbar-thumb {
          display: none !important;
        }
        
        /* Hide any resize handles */
        .gm-style-iw .ui-resizable-handle {
          display: none !important;
          visibility: hidden !important;
        }
        
        /* Force our custom window to be fully visible */
        .custom-info-window {
          overflow: visible !important;
          max-height: none !important;
        }
      `;

      document.head.appendChild(styleEl);
    };

    // Inject the global styles
    injectGlobalStyles();

    // Create markers
    markersRef.current = officeLocations.map(office => {
      // Create marker
      const marker = new window.google.maps.Marker({
        position: office.coords,
        map: map,
        title: office.name,
        icon: createMarkerIcon(office.isHeadquarters),
        animation: window.google.maps.Animation.DROP,
        zIndex: office.isHeadquarters ? 2 : 1
      });

      // Create info window with custom styles
      const infoWindow = new window.google.maps.InfoWindow({
        content: createInfoWindowContent(office),
        maxWidth: 250,
        disableAutoPan: false,
        pixelOffset: new window.google.maps.Size(0, -10)
      });

      // Add click listener to marker
      marker.addListener('click', () => {
        // Close all other info windows
        infoWindowsRef.current.forEach(window => window.close());

        // Open this info window
        infoWindow.open({
          anchor: marker,
          map
        });

        // Force re-inject styles to ensure they apply
        setTimeout(() => {
          // Hide all default close buttons again after opening
          const closeButtons = document.querySelectorAll('.gm-ui-hover-effect');
          closeButtons.forEach(button => {
            button.style.display = 'none';
            button.style.visibility = 'hidden';
          });
        }, 10);
      });

      // Add close button functionality after info window is opened
      infoWindow.addListener('domready', () => {
        // Get our custom close button
        const closeButton = document.querySelector('.info-window-close');
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            infoWindow.close();
          });
        }

        // Hide all default close buttons
        const defaultCloseButtons = document.querySelectorAll(
          '.gm-ui-hover-effect'
        );
        defaultCloseButtons.forEach(button => {
          button.style.display = 'none';
          button.style.visibility = 'hidden';
        });
      });

      infoWindowsRef.current.push(infoWindow);
      return marker;
    });

    // If an office is selected, center on it, otherwise maintain world view
    if (selectedOffice) {
      map.setCenter(selectedOffice.coords);
      map.setZoom(selectedOffice.zoomLevel || 13);

      // Open the info window for the selected office
      const markerIndex = officeLocations.findIndex(
        o => o.id === selectedOffice.id
      );
      if (markerIndex !== -1) {
        infoWindowsRef.current.forEach(window => window.close());
        infoWindowsRef.current[markerIndex].open({
          anchor: markersRef.current[markerIndex],
          map
        });
      }
    }

    // Clean up on unmount
    return () => {
      markersRef.current.forEach(marker => {
        window.google.maps.event.clearInstanceListeners(marker);
        marker.setMap(null);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      infoWindowsRef.current.forEach(window => window.close());

      // Remove injected styles
      const styleEl = document.getElementById('custom-map-styles');
      if (styleEl) styleEl.remove();
    };
  }, [mapLoaded, selectedOffice, showControls]);

  // Update map when selectedOffice changes
  useEffect(() => {
    if (!googleMapRef.current || !mapLoaded) return;

    const map = googleMapRef.current;

    if (selectedOffice) {
      map.setCenter(selectedOffice.coords);
      map.setZoom(selectedOffice.zoomLevel || 13);

      // Open the info window for the selected office
      const markerIndex = officeLocations.findIndex(
        o => o.id === selectedOffice.id
      );
      if (markerIndex !== -1) {
        infoWindowsRef.current.forEach(window => window.close());
        infoWindowsRef.current[markerIndex].open({
          anchor: markersRef.current[markerIndex],
          map
        });
      }
    } else {
      map.setCenter(worldMap.current.center);
      map.setZoom(worldMap.current.zoom);
      infoWindowsRef.current.forEach(window => window.close());
    }
  }, [selectedOffice, mapLoaded]);

  return (
    <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
      {!mapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress sx={{ color: 'var(--primary-blue)' }} />
        </Box>
      )}
      <div
        ref={mapRef}
        style={{
          width: '100%',
          height: '100%'
        }}
      />
    </Box>
  );
};

export default MapComponent;
